import React, { InputHTMLAttributes } from 'react';

import { createUseStyles } from 'react-jss';

import { Palette, Radius, Shadow, Spacing } from 'DesignSystem2/Theme';

// for cross-browser support, I referenced: https://brennaobrien.com/blog/2014/05/style-input-type-range-in-every-browser.html
const useStyles = createUseStyles({
    container: {
        display: 'flex',
        width: '100%',
        maxWidth: Spacing(40),
    },
    input: {
        cursor: 'pointer',
        width: `100%`,
        WebkitAppearance: 'none',
        margin: 0,
        backgroundColor: 'transparent',
        '&:focus': {
            outline: 'none',
        },
        '&::-webkit-slider-runnable-track': {
            height: '8px',
            background: ({ color, ratio }: { color: string; ratio: number }): string => {
                const leftColor = color;
                const rightColor = Palette.Fill.x50;
                const stop = `calc(${ratio * 100}% + ${(0.5 - ratio) * (16 + 3 * 2)}px)`;
                return `linear-gradient(to right, ${leftColor} ${stop}, ${rightColor} ${stop})`;
            },
            border: 'none',
            borderRadius: Radius.x50,
        },
        '&::-moz-range-track': {
            height: '8px',
            background: ({ color, ratio }: { color: string; ratio: number }): string => {
                const leftColor = color;
                const rightColor = Palette.Fill.x50;
                const stop = `calc(${ratio * 100}% + ${(0.5 - ratio) * (16 + 3 * 2)}px)`;
                return `linear-gradient(to right, ${leftColor} ${stop}, ${rightColor} ${stop})`;
            },
            border: 'none',
            borderRadius: Radius.x50,
        },
        '&::-ms-track': {
            WebkitAppearance: 'none',
            height: '8px',
            background: ({ color, ratio }: { color: string; ratio: number }): string => {
                const leftColor = color;
                const rightColor = Palette.Fill.x50;
                const stop = `calc(${ratio * 100}% + ${(0.5 - ratio) * (48 + 3 * 2)}px)`;
                return `linear-gradient(to right, ${leftColor} ${stop}, ${rightColor} ${stop})`;
            },
            border: 'none',
            borderRadius: Radius.x50,
            boxShadow: Shadow.x10,
        },
        '&::-webkit-slider-thumb': {
            WebkitAppearance: 'none',
            boxSizing: 'content-box',
            position: 'relative',
            top: `calc(50% - ${Spacing(3)})`,
            left: ({
                value,
                min,
                max,
            }: {
                color: string;
                ratio: number;
                value: number;
                min: number;
                max: number;
            }): string => `${max === min ? 0 : (28 * value) / (max - min)}px`,
            height: Spacing(2),
            width: Spacing(2),
            padding: Spacing(2),
            borderRadius: Radius.x50,
            marginLeft: '-14px',
            marginRight: '-14px',
            background: `radial-gradient(circle at 50% 50%, ${Palette.Fill.x10} 30%, rgba(255, 255, 0, 0) 33%)`,
        },
        '&::-moz-range-thumb': {
            boxSizing: 'content-box',
            position: 'relative',
            top: `calc(50% - ${Spacing(3)})`,
            left: ({
                value,
                min,
                max,
            }: {
                color: string;
                ratio: number;
                value: number;
                min: number;
                max: number;
            }): string => `${max === min ? 0 : (28 * value) / (max - min)}px`,
            height: Spacing(2),
            width: Spacing(2),
            padding: Spacing(2),
            borderRadius: Radius.x50,
            marginLeft: '-14px',
            marginRight: '-14px',
            background: `radial-gradient(circle at 50% 50%, ${Palette.Fill.x10} 30%, rgba(255, 255, 0, 0) 33%)`,
        },
        '&::-ms-thumb': {
            boxSizing: 'content-box',
            position: 'relative',
            top: `calc(50% - ${Spacing(3)})`,
            left: ({
                value,
                min,
                max,
            }: {
                color: string;
                ratio: number;
                value: number;
                min: number;
                max: number;
            }): string => `${max === min ? 0 : (28 * value) / (max - min)}px`,
            height: Spacing(2),
            width: Spacing(2),
            padding: Spacing(2),
            borderRadius: Radius.x50,
            marginLeft: '-14px',
            marginRight: '-14px',
            background: `radial-gradient(circle at 50% 50%, ${Palette.Fill.x10} 30%, rgba(255, 255, 0, 0) 33%)`,
        },
    },
});

interface SliderProps extends InputHTMLAttributes<HTMLInputElement> {
    color: string;
    min: number;
    max: number;
    value: number;
}

const Slider = ({
    id,
    form,
    name,
    color = Palette.Fill.x20,
    value,
    min = 0,
    max = 1,
    step = 1,
    onChange,
}: SliderProps): JSX.Element => {
    const classes = useStyles({ color, ratio: (value - min) / (max - min), value, min, max });
    return (
        <div className={classes.container}>
            <input
                type={'range'}
                className={classes.input}
                id={id}
                form={form}
                name={name}
                value={value}
                min={min}
                max={max}
                step={step}
                onChange={onChange}
                style={{ width: max === 0 ? 0 : undefined }}
            />
        </div>
    );
};

export default Slider;
